import { useState, useEffect } from 'react';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { X, ChevronLeft, ChevronRight, ZoomIn, Play, Film, ExternalLink, MapPin } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const GALLERY_FILTERS = [
  { key: 'salle', label: 'Salle', tagline: 'Un espace de reception de 150 convives, en rez de jardin' },
  { key: 'gite', label: 'Gite', tagline: '20 couchages au coeur du Parc du Pilat' },
  { key: 'appartement-1', label: "L'Appartement Terrasse", tagline: 'Plain-pied et terrasse privee, 8 couchages' },
  { key: 'appartement-2', label: "L'Appartement Panorama", tagline: 'Vue degagee sur la foret et la vallee du Furan, 8 couchages' },
  { key: 'exterieurs', label: 'Exterieurs', tagline: 'Nature preservee, parking 50 places et espaces detente' },
  { key: 'alentours', label: 'Alentours', tagline: 'Gouffre d\u2019Enfer, barrages et sentiers a deux pas' },
  { key: 'evenements', label: 'Evenements', tagline: 'Mariages, anniversaires et seminaires inoubliables' },
];

const PAGE_LABELS = {
  'salle': 'Salle',
  'gite': 'Gite',
  'appartement-1': 'Apt Terrasse',
  'appartement-2': 'Apt Panorama',
  'exterieurs': 'Exterieurs',
  'alentours': 'Alentours',
  'evenements': 'Evenements',
};

function mediaFileUrl(filename) {
  return `${BACKEND_URL}/api/media/file/${filename}`;
}

export default function Gallery() {
  const [ref, isVisible] = useScrollAnimation(0.1);
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('salle');
  const [lightbox, setLightbox] = useState(null);

  // Read filter from URL hash (e.g. #galerie-gite)
  useEffect(() => {
    const readHash = () => {
      const hash = window.location.hash;
      const match = hash.match(/#galerie-(.+)/);
      if (match) {
        const key = match[1];
        if (GALLERY_FILTERS.some(f => f.key === key)) {
          setFilter(key);
        }
      }
    };
    readHash();
    window.addEventListener('hashchange', readHash);
    return () => window.removeEventListener('hashchange', readHash);
  }, []);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/media/gallery`).then(r => {
      setItems(r.data || []);
    }).catch(() => {});
  }, []);

  const filtered = items.filter(i => i.page === filter);
  // Videos first, then images
  const videos = filtered.filter(i => i.type === 'video' || i.type === 'embed');
  const images = filtered.filter(i => i.type === 'image');
  // For lightbox navigation, only non-external-link items
  const lightboxItems = filtered.filter(i => !(i.page === 'alentours' && i.external_link));

  const openItem = (item, idx) => {
    // Alentours with external link -> open in new tab
    if (item.page === 'alentours' && item.external_link) {
      window.open(item.external_link, '_blank', 'noopener,noreferrer');
      return;
    }
    // Find index in lightboxItems
    const lbIdx = lightboxItems.findIndex(i => i.id === item.id);
    if (lbIdx >= 0) setLightbox(lbIdx);
  };

  const closeLightbox = () => setLightbox(null);
  const nav = (dir) => {
    if (lightbox === null) return;
    const next = lightbox + dir;
    if (next >= 0 && next < lightboxItems.length) setLightbox(next);
  };

  useEffect(() => {
    const handleKey = (e) => {
      if (lightbox === null) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') nav(-1);
      if (e.key === 'ArrowRight') nav(1);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  });

  function getThumb(item) {
    const f = item.files || {};
    if (f.thumb) return mediaFileUrl(f.thumb);
    if (f.mobile) return mediaFileUrl(f.mobile);
    if (f.desktop) return mediaFileUrl(f.desktop);
    return null;
  }

  function getFullSrc(item) {
    const f = item.files || {};
    if (item.type === 'embed') return f.embed_url;
    if (f.desktop) return mediaFileUrl(f.desktop);
    if (f.mobile) return mediaFileUrl(f.mobile);
    if (f.original) return mediaFileUrl(f.original);
    return null;
  }

  const isVideo = (item) => item.type === 'video' || item.type === 'embed';
  const hasExtLink = (item) => item.page === 'alentours' && item.external_link;

  return (
    <section id="galerie" data-testid="gallery-section" className="py-24 md:py-32 noise-bg">
      <div ref={ref} className={`max-w-[1400px] mx-auto px-6 md:px-12 fade-in-section ${isVisible ? 'is-visible' : ''}`}>
        <div className="text-center mb-12">
          <span className="text-[#8A9A5B] text-sm font-medium tracking-[0.2em] uppercase">Explorez notre domaine</span>
          <h2 data-testid="gallery-title" className="font-['Playfair_Display'] text-3xl md:text-5xl text-[#2C3E30] mt-3 tracking-tight">
            Galerie Photos & Videos
          </h2>
        </div>

        {/* Filter pills */}
        <div className="flex flex-wrap justify-center gap-2 mb-12">
          {GALLERY_FILTERS.map(c => (
            <Button
              key={c.key}
              data-testid={`gallery-filter-${c.key}`}
              variant={filter === c.key ? 'default' : 'outline'}
              onClick={() => setFilter(c.key)}
              className={`rounded-full px-5 py-2 text-sm transition-all ${
                filter === c.key
                  ? 'bg-[#2C3E30] text-[#F2F0E9] hover:bg-[#3a5040]'
                  : 'border-[#E5E5E0] text-[#595959] hover:bg-[#F5F5F0] hover:text-[#2C3E30]'
              }`}
            >
              {c.label}
            </Button>
          ))}
        </div>

        {/* Tagline of the active filter */}
        <p data-testid="gallery-filter-tagline" className="text-center text-sm text-[#8A9A5B] italic -mt-8 mb-12 tracking-wide">
          {GALLERY_FILTERS.find(c => c.key === filter)?.tagline}
        </p>

        {/* Videos section */}
        {videos.length > 0 && (
          <div className="mb-10">
            <h3 className="text-sm font-semibold text-[#2C3E30] mb-4 flex items-center gap-2">
              <Play className="w-4 h-4 text-[#8A9A5B]" strokeWidth={1.5} /> Videos
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {videos.map(item => (
                <div key={item.id} className="rounded-xl overflow-hidden bg-black">
                  {item.type === 'video' ? (
                    <video
                      src={getFullSrc(item)}
                      className="w-full aspect-video"
                      controls
                      preload="metadata"
                      playsInline
                    />
                  ) : (
                    <iframe
                      src={getFullSrc(item)}
                      className="w-full aspect-video"
                      allowFullScreen
                      title={item.title}
                    />
                  )}
                  <p className="text-white/80 text-sm p-3 bg-[#2C3E30]">{item.title}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Alentours: cartes points d'interet (titre + texte + distance + photo) */}
        {filter === 'alentours' && images.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="gallery-alentours-cards">
            {images.map((item) => (
              <div
                key={item.id}
                data-testid={`gallery-poi-${item.id}`}
                className="stagger-child bg-white rounded-xl overflow-hidden shadow-[0_2px_12px_-4px_rgba(0,0,0,0.08)] flex flex-col group"
              >
                <div className="relative overflow-hidden cursor-pointer" onClick={() => openItem(item, 0)}>
                  <img src={getThumb(item)} alt={item.title} className="w-full h-52 object-cover card-image-zoom" loading="lazy" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
                    {hasExtLink(item)
                      ? <ExternalLink className="w-7 h-7 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" strokeWidth={1.5} />
                      : <ZoomIn className="w-7 h-7 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" strokeWidth={1.5} />}
                  </div>
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <h3 className="font-['Playfair_Display'] text-lg text-[#2C3E30] mb-2">{item.title}</h3>
                  {item.caption && <p className="text-sm text-[#595959] leading-relaxed flex-1">{item.caption}</p>}
                  {item.distance && (
                    <p className="mt-3 text-xs font-medium text-[#8A9A5B] flex items-center gap-1.5">
                      <MapPin className="w-3.5 h-3.5" strokeWidth={1.5} /> {item.distance}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
        <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 space-y-4">
          {images.map((item, idx) => (
            <div
              key={item.id}
              data-testid={`gallery-item-${item.id}`}
              className="stagger-child break-inside-avoid group relative rounded-xl overflow-hidden cursor-pointer"
              onClick={() => openItem(item, idx)}
            >
              {item.type === 'image' ? (
                <img src={getThumb(item)} alt={item.title} className="w-full object-cover card-image-zoom" loading="lazy" />
              ) : item.type === 'video' ? (
                <div className="w-full aspect-video bg-[#2C3E30]/10 flex items-center justify-center">
                  <Play className="w-12 h-12 text-[#2C3E30]/50" />
                </div>
              ) : (
                <div className="w-full aspect-video bg-[#2C3E30]/10 flex items-center justify-center">
                  <Film className="w-12 h-12 text-[#2C3E30]/50" />
                </div>
              )}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300 flex items-center justify-center">
                {hasExtLink(item) ? (
                  <ExternalLink className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" strokeWidth={1.5} />
                ) : isVideo(item) ? (
                  <Play className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" strokeWidth={1.5} />
                ) : (
                  <ZoomIn className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" strokeWidth={1.5} />
                )}
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <p className="text-white text-sm font-medium">{item.title}</p>
                <div className="flex gap-1.5 mt-1">
                  <Badge className="bg-white/20 text-white border-none text-xs backdrop-blur-sm">{PAGE_LABELS[item.page] || item.page}</Badge>
                  {isVideo(item) && <Badge className="bg-purple-500/60 text-white border-none text-xs backdrop-blur-sm">Video</Badge>}
                  {hasExtLink(item) && <Badge className="bg-blue-500/60 text-white border-none text-xs backdrop-blur-sm">Lien</Badge>}
                </div>
              </div>
            </div>
          ))}
        </div>
        )}

        {items.length === 0 && (
          <div className="text-center py-16">
            <p className="text-[#595959]">La galerie sera bientot disponible.</p>
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightbox !== null && lightboxItems[lightbox] && (
        <div data-testid="gallery-lightbox" className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center" onClick={closeLightbox}>
          <button data-testid="lightbox-close" onClick={closeLightbox} className="absolute top-6 right-6 text-white/70 hover:text-white z-10">
            <X className="w-8 h-8" strokeWidth={1.5} />
          </button>
          {lightbox > 0 && (
            <button data-testid="lightbox-prev" onClick={e => { e.stopPropagation(); nav(-1); }} className="absolute left-4 md:left-8 text-white/70 hover:text-white z-10 p-2">
              <ChevronLeft className="w-10 h-10" strokeWidth={1.5} />
            </button>
          )}
          {lightbox < lightboxItems.length - 1 && (
            <button data-testid="lightbox-next" onClick={e => { e.stopPropagation(); nav(1); }} className="absolute right-4 md:right-8 text-white/70 hover:text-white z-10 p-2">
              <ChevronRight className="w-10 h-10" strokeWidth={1.5} />
            </button>
          )}
          <div className="max-w-5xl max-h-[85vh] mx-4" onClick={e => e.stopPropagation()}>
            {(() => {
              const current = lightboxItems[lightbox];
              if (current.type === 'video') {
                return <video src={getFullSrc(current)} className="max-w-full max-h-[80vh] rounded-lg" controls autoPlay />;
              }
              if (current.type === 'embed') {
                return <iframe src={getFullSrc(current)} className="w-[80vw] max-w-4xl aspect-video rounded-lg" allowFullScreen title={current.title} />;
              }
              return <img src={getFullSrc(current)} alt={current.title} className="max-w-full max-h-[80vh] object-contain rounded-lg" />;
            })()}
            <p className="text-center text-white/80 mt-4 text-sm font-medium">
              {lightboxItems[lightbox].title}
              <span className="text-white/50 ml-3">{lightbox + 1} / {lightboxItems.length}</span>
            </p>
          </div>
        </div>
      )}
    </section>
  );
}
